"use client";

import { useQuery } from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { useState } from "react";
import { checkBuddyStatus, checkExistingBuddyRequest } from "@/actions/buddy";
import { useAuthPrompt } from "@/components/auth";
import { ConnectButton } from "@/components/buddy";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { Button } from "@/components/ui/button";
import { trackEntityShare } from "@/lib/analytics/share";
import type { BuddyRelationship } from "@/lib/buddy/relationship";
import { BuddyManageDrawer } from "./buddy-request/buddy-manage-drawer";
import { BuddyRequestDrawer } from "./buddy-request/buddy-request-drawer";
import { publicProfileActionKeys } from "./public-profile-actions.keys";
import type { PublicProfileActionsProps } from "./public-profile-actions.types";
import { PublicProfileActionsFallback } from "./public-profile-actions-fallback";

const ACTION_QUERY_STALE_TIME_MS = 30_000;

const fallbackBuddyStatus = {
  isBuddy: false,
  blockedByMe: false,
  blockedByThem: false,
  connectionStatus: null,
  mutePosts: false,
  muteStories: false,
};

const fallbackExistingRequest = { type: "none" as const };

export function PublicProfileActionsClient({
  targetUserId,
  targetName,
  profile,
  isAuthenticated = true,
}: PublicProfileActionsProps) {
  const { openAuthPrompt } = useAuthPrompt();
  const [shareOpen, setShareOpen] = useState(false);
  const existingRequestQuery = useQuery({
    queryKey: publicProfileActionKeys.existingRequest(targetUserId),
    queryFn: () => checkExistingBuddyRequest(targetUserId),
    staleTime: ACTION_QUERY_STALE_TIME_MS,
    enabled: isAuthenticated,
  });

  const buddyStatusQuery = useQuery({
    queryKey: publicProfileActionKeys.buddyStatus(targetUserId),
    queryFn: () => checkBuddyStatus(targetUserId),
    staleTime: ACTION_QUERY_STALE_TIME_MS,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) {
    return (
      <Button
        type="button"
        variant="ghost"
        // The signed-out twin of ConnectButton's `solid` Connect.
        className="h-8 w-[6.5rem] rounded-lg bg-brand px-3 text-[13px] leading-none font-semibold text-white hover:bg-brand-hover dark:bg-brand dark:text-white dark:hover:bg-brand-hover"
        onClick={() =>
          openAuthPrompt({
            title: "Sign in to connect",
            description: "Use your Hiive account to send buddy requests.",
          })
        }
      >
        Connect
      </Button>
    );
  }

  if (
    (existingRequestQuery.isPending && !existingRequestQuery.data) ||
    (buddyStatusQuery.isPending && !buddyStatusQuery.data)
  ) {
    return <PublicProfileActionsFallback />;
  }

  const existingRequest = existingRequestQuery.data?.ok
    ? existingRequestQuery.data.data
    : fallbackExistingRequest;
  const buddyStatus = buddyStatusQuery.data?.ok
    ? buddyStatusQuery.data.data
    : fallbackBuddyStatus;
  const canInteract = !buddyStatus.blockedByMe && !buddyStatus.blockedByThem;

  return (
    <>
      {canInteract && (
        <ProfileConnectActions
          targetUserId={targetUserId}
          targetName={targetName}
          profile={profile}
          existingRequest={existingRequest}
          isAlreadyBuddy={buddyStatus.isBuddy}
        />
      )}
      <BuddyManageDrawer
        targetUserId={targetUserId}
        targetName={targetName}
        isBuddy={buddyStatus.isBuddy}
        blockedByMe={buddyStatus.blockedByMe}
        mutePosts={buddyStatus.mutePosts}
        muteStories={buddyStatus.muteStories}
        onShare={() => {
          trackEntityShare("profile", targetUserId, {
            surface: "entity_page",
          });
          setShareOpen(true);
        }}
      />
      {/* Owned here, not inside the options sheet, so it can open once that
          sheet has closed instead of stacking on it. `nativeShare` is what
          turns on "Share…" and "Copy link"; the URL falls back to this page. */}
      <SendToChatDrawer
        open={shareOpen}
        onOpenChange={setShareOpen}
        payload={{
          type: "entity",
          entityType: "profile",
          entityId: targetUserId,
        }}
        nativeShare={{
          title: targetName ? `${targetName} on Hiive` : "Profile on Hiive",
          surface: "profile_page",
        }}
      />
    </>
  );
}

type ProfileConnectActionsProps = {
  targetUserId: string;
  targetName: string | null;
  profile: PublicProfileActionsProps["profile"];
  existingRequest: { type: "sent" | "received" | "none" };
  isAlreadyBuddy: boolean;
};

/**
 * The profile is the one surface with room for both paths: a one-tap add as
 * the primary, and the multi-step drawer behind an explicit "with a note"
 * button for people who actually want to say something. Cards elsewhere only
 * get the one-tap — the whole point is that scrolling surfaces ask nothing.
 */
function ProfileConnectActions({
  targetUserId,
  targetName,
  profile,
  existingRequest,
  isAlreadyBuddy,
}: ProfileConnectActionsProps) {
  const serverRelationship: BuddyRelationship = isAlreadyBuddy
    ? "buddy"
    : existingRequest.type === "sent"
      ? "request_sent"
      : existingRequest.type === "received"
        ? "request_received"
        : "none";

  // Only set once the viewer acts here. Deriving rather than seeding state
  // means a refetch that changes the server answer is picked up, while an
  // optimistic add still wins over a server read that has not caught up.
  const [actedRelationship, setActedRelationship] =
    useState<BuddyRelationship | null>(null);
  const relationship = actedRelationship ?? serverRelationship;

  // Already connected: the drawer owns this, because it also carries the
  // open-chat affordance and the rest of the buddy controls.
  if (relationship === "buddy") {
    return (
      <BuddyRequestDrawer
        targetUserId={targetUserId}
        targetName={targetName}
        profile={profile}
        isAlreadyBuddy
      />
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* ConnectButton renders every not-yet-connected state itself — none,
          requested, and respond. Handing "requested" back to the drawer made
          it re-render its own trigger from the *server* `existingRequest`,
          which still said "none" until the refetch landed: the old "Add as
          buddy" button flashed up for a moment right after tapping Add. */}
      <ConnectButton
        targetUserId={targetUserId}
        targetName={targetName}
        relationship={relationship}
        surface="profile"
        // Same filled "Connect" as the people rows on /discover, so adding
        // someone looks the same wherever you meet them.
        appearance="solid"
        onRelationshipChange={setActedRelationship}
      />
      {/* The note path only makes sense before a request exists. */}
      {relationship === "none" ? (
        <BuddyRequestDrawer
          targetUserId={targetUserId}
          targetName={targetName}
          profile={profile}
          isAlreadyBuddy={false}
          onRequestSent={() => setActedRelationship("request_sent")}
          trigger={
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-lg"
              aria-label={
                targetName
                  ? `Add ${targetName.split(" ")[0]} with a note`
                  : "Add with a note"
              }
            >
              <PenLine className="size-4" />
            </Button>
          }
        />
      ) : null}
    </div>
  );
}
